// The waste log screen (kitchen flow, Stage 2C): record what is thrown away —
// what, how much, why, which batch, an optional photo — approved with a
// manager PIN, and see what was thrown away lately with its value.
// Used on the kitchen's Production page and the store's Inventory screen.
// Strings: lib/inventoryFlow/wasteStrings.ts. Rules: wasteLogic.ts.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, Loader2, Trash2, X } from 'lucide-react'
import apiClient from '@/api/client'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn, formatDateTimeHuman } from '@/lib/utils'
import { useCurrency } from '@/contexts/CurrencyContext'
import { getStoredUserRole } from '@/lib/staff-roles'
import { intentScope, useIntentKey } from '@/lib/inventoryFlow/intentKey'
import { shrinkPhoto } from '@/lib/shrinkPhoto'
import type { FlowItem, FlowLocation } from '@/lib/inventoryFlow/types'
import type { WasteEntry, WasteReason } from '@/lib/inventoryFlow/wasteTypes'
import { WT } from '@/lib/inventoryFlow/wasteStrings'
import { WASTE_REASONS, estimateWasteValue, expiryWords, wasteFormProblem, wasteSourceChip } from '@/lib/inventoryFlow/wasteLogic'
import { useItemScanner } from '@/components/store/inventory/useItemScanner'
import { QtyStepper } from './QtyStepper'
import { LoadError } from './LoadError'

type Notify = (kind: 'success' | 'error', message: string) => void

function errMessage(e: unknown): string {
  return friendlyError(e, { brief: true })
}

/** Which places this role may throw away from (mirrors wasteRoleMayUse). */
function placesFor(role: string, locations: FlowLocation[]): FlowLocation[] {
  const active = locations.filter((l) => l.is_active && l.kind !== 'transit')
  if (role === 'kitchen_manager') return active.filter((l) => l.kind === 'kitchen')
  if (role === 'inventory_manager') return active.filter((l) => l.kind === 'store')
  return active
}

export function WasteTab({ notify }: { notify: Notify }) {
  const role = getStoredUserRole()
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const { data: locRes } = useQuery({ queryKey: ['inventory-flow', 'locations'], queryFn: () => apiClient.getFlowLocations() })
  const places = useMemo(() => placesFor(role, locRes?.data ?? []), [role, locRes])
  const [placeId, setPlaceId] = useState<string>('')
  useEffect(() => {
    if (!placeId && places.length > 0) {
      const preferred = places.find((p) => (role === 'kitchen_manager' ? p.is_sale_source : p.is_receiving_default)) ?? places[0]
      setPlaceId(preferred.id)
    }
  }, [places, placeId, role])

  const { data: itemsRes, isError: itemsError, refetch: refetchItems, isFetching: itemsFetching } = useQuery({ queryKey: ['inventory-flow', 'items'], queryFn: () => apiClient.getFlowItems(), staleTime: 60_000 })
  const items = itemsRes?.data ?? []
  const [search, setSearch] = useState('')
  const [item, setItem] = useState<FlowItem | null>(null)
  const [qty, setQty] = useState(0)
  const [reason, setReason] = useState<WasteReason | null>(null)
  const [batchId, setBatchId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [pin, setPin] = useState('')
  const [photo, setPhoto] = useState<{ content_type: string; data_base64: string } | null>(null)
  const [photoBusy, setPhotoBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const pick = (it: FlowItem) => {
    setItem(it)
    setBatchId(null)
    setQty(0)
    setSearch('')
  }
  const scanner = useItemScanner({
    enabled: !item,
    onItem: (m) => {
      const found = items.find((i) => i.id === m.item.id)
      if (!found) return { ok: false, text: m.item.name }
      pick(found)
      setQty(m.pack_qty)
      return { ok: true, text: m.item.name }
    },
  })

  const { data: lotsRes } = useQuery({
    queryKey: ['inventory-flow', 'lots', item?.id, placeId],
    queryFn: () => apiClient.getItemLotsAt(item!.id, placeId),
    enabled: Boolean(item && placeId),
  })
  const lots = lotsRes?.data?.lots ?? []
  const estimate = estimateWasteValue(qty, lots, batchId, lots[0]?.unit_cost ?? null)
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return []
    return items.filter((i) => i.name.toLowerCase().includes(q) || (i.code ?? '').toLowerCase().includes(q)).slice(0, 20)
  }, [items, search])

  // One key per waste entry as typed: a retry of the same entry reuses it, a
  // different item / amount / reason (or any other field) is a new entry, and
  // a success starts a new one.
  const intent = useIntentKey(intentScope('waste', placeId, item?.id ?? null, qty, reason, batchId, note.trim(), photo ? photo.data_base64.length : 0))
  const problem = wasteFormProblem({ itemId: item?.id ?? null, qty, reason, note, pin })
  const save = useMutation({
    mutationFn: () =>
      apiClient.recordWaste({
        stock_item_id: item!.id,
        location_id: placeId || undefined,
        qty,
        reason: reason!,
        note: note.trim() || undefined,
        batch_id: batchId ?? undefined,
        pin,
        ...(photo ? { photo_content_type: photo.content_type, photo_base64: photo.data_base64 } : {}),
      }, intent.key),
    onSuccess: (res) => {
      const v = res.data
      notify('success', WT.saved(v?.item_name ?? item?.name ?? ''))
      if (v?.shortfall && v.shortfall > 0) notify('error', WT.shortfall(String(v.shortfall), v.unit))
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      setItem(null)
      setQty(0)
      setReason(null)
      setBatchId(null)
      setNote('')
      setPin('')
      setPhoto(null)
      intent.renew()
    },
    onError: (e) => {
      notify('error', errMessage(e))
      setPin('')
    },
  })

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(20rem,28rem)]">
      <section className="space-y-4 rounded-lg border-2 border-red-200 p-4 dark:border-red-900">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold"><Trash2 className="h-4 w-4" /> {WT.title}</h3>
          <p className="text-sm text-muted-foreground">{WT.help}</p>
        </div>

        {places.length > 1 && (
          <div className="space-y-1.5">
            <span className="text-sm font-medium">{WT.place}</span>
            <div className="flex flex-wrap gap-2">
              {places.map((p) => (
                <button key={p.id} type="button" aria-pressed={placeId === p.id} onClick={() => { setPlaceId(p.id); setBatchId(null) }}
                  className={cn('h-10 rounded-lg border-2 px-4 text-sm font-semibold touch-manipulation',
                    placeId === p.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:bg-muted')}>
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <span className="text-sm font-medium">{WT.item}</span>
          {item ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
              <span className="text-base font-semibold">{item.name} <span className="text-sm font-normal text-muted-foreground">({item.unit})</span></span>
              <Button type="button" variant="outline" onClick={() => setItem(null)}>{WT.change}</Button>
            </div>
          ) : (
            <>
              {scanner.ui}
              <Input placeholder={WT.searchItem} value={search} onChange={(e) => setSearch(e.target.value)} />
              {itemsError && !itemsRes && <LoadError busy={itemsFetching} onRetry={() => void refetchItems()} />}
              {matches.length > 0 && (
                <ul className="max-h-60 divide-y overflow-y-auto rounded-lg border">
                  {matches.map((i) => (
                    <li key={i.id}>
                      <button type="button" onClick={() => pick(i)} className="flex w-full items-center justify-between gap-3 p-3 text-left hover:bg-muted touch-manipulation">
                        <span className="font-medium">{i.name}</span>
                        <span className="text-sm text-muted-foreground">{WT.has} {i.balances[placeId] ?? 0} {i.unit}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        {item && (
          <>
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{WT.qty}</span>
              <QtyStepper value={qty} unit={item.unit} label={item.name} min={0} onChange={setQty} />
            </div>

            <div className="space-y-1.5">
              <span className="text-sm font-medium">{WT.reason}</span>
              <div className="flex flex-wrap gap-2">
                {WASTE_REASONS.map((r) => (
                  <button key={r} type="button" aria-pressed={reason === r} onClick={() => setReason(r)}
                    className={cn('min-h-10 rounded-full border-2 px-4 text-sm font-medium touch-manipulation',
                      reason === r ? 'border-red-600 bg-red-600 text-white' : 'border-border hover:bg-muted/40')}>
                    {WT.reasons[r]}
                  </button>
                ))}
              </div>
            </div>

            {lots.length > 0 && (
              <div className="space-y-1.5">
                <span className="text-sm font-medium">{WT.batch}</span>
                <div className="flex flex-col gap-2">
                  <label className="flex min-h-10 cursor-pointer items-center gap-3 rounded-lg border p-2">
                    <input type="radio" className="h-4 w-4" checked={batchId === null} onChange={() => setBatchId(null)} />
                    <span>{WT.batchAuto}</span>
                  </label>
                  {lots.map((l) => {
                    const words = expiryWords(l.days_until_expiry, WT)
                    const expired = (l.days_until_expiry ?? 1) < 0
                    return (
                      <label key={l.id} className={cn('flex min-h-10 cursor-pointer items-center gap-3 rounded-lg border p-2', expired && 'border-red-400 bg-red-50 dark:bg-red-950/30')}>
                        <input type="radio" className="h-4 w-4" checked={batchId === l.id} onChange={() => setBatchId(l.id)} />
                        <span className="flex-1">
                          <span className="font-medium">{l.lot_no ?? l.received_at.slice(0, 10)}</span>
                          <span className={cn('ml-2 text-sm', expired ? 'font-semibold text-red-700 dark:text-red-300' : 'text-muted-foreground')}>{words ?? WT.noBatch}</span>
                        </span>
                        <span className="tabular-nums text-sm">{l.quantity_remaining} {item.unit}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            )}

            <label className="block space-y-1.5">
              <span className="text-sm font-medium">{reason === 'other' ? WT.noteRequired : WT.note}</span>
              <Input maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>

            <div className="flex flex-wrap items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  if (!f) return
                  setPhotoBusy(true)
                  try {
                    setPhoto(await shrinkPhoto(f))
                  } catch (err) {
                    notify('error', errMessage(err))
                  } finally {
                    setPhotoBusy(false)
                  }
                }} />
              {photo ? (
                <Button type="button" variant="outline" onClick={() => setPhoto(null)}><X className="mr-2 h-4 w-4" /> {WT.photoAdded} · {WT.removePhoto}</Button>
              ) : (
                <Button type="button" variant="outline" disabled={photoBusy} onClick={() => fileRef.current?.click()}>
                  {photoBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />} {WT.photo}
                </Button>
              )}
              {qty > 0 && (
                <span className="text-sm">
                  {WT.value}: <strong className="tabular-nums">{formatCurrency(estimate)}</strong>
                  <span className="block text-xs text-muted-foreground">{WT.valueHint}</span>
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <label className="space-y-1.5">
                <span className="text-sm font-medium">{WT.pin}</span>
                <Input type="password" inputMode="numeric" autoComplete="off" className="w-44 tracking-widest" value={pin} onChange={(e) => setPin(e.target.value)} />
              </label>
              <Button type="button" variant="destructive" className="flex-1" disabled={problem !== null || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? WT.saving : WT.save}
              </Button>
            </div>
          </>
        )}
      </section>

      <RecentWaste placeId={placeId} />
    </div>
  )
}

function RecentWaste({ placeId }: { placeId: string }) {
  const { formatCurrency } = useCurrency()
  const [days, setDays] = useState(7)
  const from = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() - (days - 1))
    const p = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
  }, [days])
  const { data } = useQuery({
    queryKey: ['inventory-flow', 'waste', placeId, from],
    queryFn: () => apiClient.getWasteLog({ from, location_id: placeId || undefined, limit: 200 }),
    enabled: Boolean(placeId),
  })
  const log = data?.data
  const entries: WasteEntry[] = log?.entries ?? []
  return (
    <section className="space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold">{WT.recent}</h3>
        <div className="flex gap-1">
          {[7, 30].map((n) => (
            <Button key={n} type="button" size="sm" variant={days === n ? 'default' : 'outline'} className="h-9" onClick={() => setDays(n)}>{WT.days(n)}</Button>
          ))}
        </div>
      </div>
      {log && <p className="text-sm">{WT.total}: <strong className="tabular-nums">{formatCurrency(log.total_value)}</strong></p>}
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">{WT.none}</p>
      ) : (
        <ul className="divide-y">
          {entries.map((e) => (
            <li key={e.id} className="space-y-0.5 py-2">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium">{e.item_name}</span>
                <span className="tabular-nums font-semibold">{formatCurrency(e.value)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                <span>{e.quantity} {e.unit} · {WT.reasons[e.reason]}</span>
                {wasteSourceChip(e.source) && <span className="rounded-full bg-muted px-2 text-xs">{wasteSourceChip(e.source)}</span>}
                {e.lot_no && <span>· {e.lot_no}</span>}
                {e.has_photo && <WastePhoto id={e.id} />}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatDateTimeHuman(e.created_at)}
                {e.recorded_by_name ? ` · ${WT.by} ${e.recorded_by_name}` : ''}
                {e.authorized_by_name ? ` · ${WT.approvedBy}: ${e.authorized_by_name}` : ''}
                {e.note ? ` · ${e.note}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** A small "photo" link that opens the picture (fetched with the auth header). */
function WastePhoto({ id }: { id: string }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  return (
    <>
      <button type="button" className="underline" onClick={async () => setUrl(URL.createObjectURL(await apiClient.getWastePhotoBlob(id)))}>📷</button>
      {url && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={() => setUrl(null)}>
          <img src={url} alt="" className="max-h-[85vh] max-w-full rounded-md object-contain" />
        </div>
      )}
    </>
  )
}
